// frontend/src/hooks/useDashboard.ts
import { useState, useEffect, useCallback } from 'react';
import { dashboardService } from '../services/dashboardService';
import {
  DashboardMetrics,
  RecentSale,
  SalesChartData,
  TopProduct,
  LowStockProduct,
  RevenueComparison
} from '../types';

/**
 * Hook personalizado para gestionar el estado del Dashboard
 * Centraliza la lógica de fetching y manejo de errores
 */
export const useDashboard = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [recentSales, setRecentSales] = useState<RecentSale[]>([]);
  const [salesChartData, setSalesChartData] = useState<SalesChartData[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [lowStockProducts, setLowStockProducts] = useState<LowStockProduct[]>([]);
  const [revenueComparison, setRevenueComparison] = useState<RevenueComparison | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Carga las métricas principales
   */
  const loadMetrics = useCallback(async () => {
    try {
      const data = await dashboardService.getMetrics();
      setMetrics(data);
    } catch (err: any) {
      console.error('Error cargando métricas:', err);
      // Solo mostrar error si no es un error de "sin datos"
      if (err.response?.status !== 404) {
        throw err;
      }
    }
  }, []);

  /**
   * Carga las ventas recientes
   */
  const loadRecentSales = useCallback(async (limit: number = 10) => {
    try {
      const data = await dashboardService.getRecentSales(limit);
      setRecentSales(data);
    } catch (err: any) {
      console.error('Error cargando ventas recientes:', err);
      if (err.response?.status !== 404) {
        throw err;
      }
    }
  }, []);

  /**
   * Carga datos del gráfico de ventas
   */
  const loadSalesChart = useCallback(async (period: 'week' | 'month' | 'year' = 'month') => {
    try {
      const data = await dashboardService.getSalesChartData(period);
      setSalesChartData(data);
    } catch (err: any) {
      console.error('Error cargando gráfico de ventas:', err);
      if (err.response?.status !== 404) {
        throw err;
      }
    }
  }, []);

  /**
   * Carga productos más vendidos
   */
  const loadTopProducts = useCallback(async (limit: number = 5) => {
    try {
      const data = await dashboardService.getTopProducts(limit);
      setTopProducts(data);
    } catch (err: any) {
      console.error('Error cargando productos top:', err);
      if (err.response?.status !== 404) {
        throw err;
      }
    }
  }, []);

  /**
   * Carga productos con stock bajo
   */
  const loadLowStockProducts = useCallback(async (limit: number = 10) => {
    try {
      const data = await dashboardService.getLowStockProducts(limit);
      setLowStockProducts(data);
    } catch (err: any) {
      console.error('Error cargando productos con stock bajo:', err);
      if (err.response?.status !== 404) {
        throw err;
      }
    }
  }, []);

  /**
   * Carga todos los datos del dashboard de forma consolidada
   */
  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Usar el endpoint consolidado para reducir conexiones en producción
      const summary = await dashboardService.getSummary(5);
      
      setMetrics(summary.metrics);
      setRecentSales(summary.recentSales);
      
      // Adaptar el formato de comparison si es necesario
      if (summary.comparison) {
        setRevenueComparison({
          currentMonth: {
            revenue: summary.metrics.totalRevenue,
            salesCount: summary.metrics.salesCount
          },
          lastMonth: {
            revenue: 0, // No enviado individualmente para ahorrar bytes, pero calculado en el cambio %
            salesCount: 0
          },
          comparison: {
            revenueChange: summary.comparison.revenueChange,
            salesChange: 0,
            revenueGrowth: summary.comparison.revenueGrowth,
            salesGrowth: false
          }
        });
      }

      // Cargar otros datos secundarios (pueden ser opcionales o cargarse bajo demanda)
      // await loadSalesChart().catch(e => console.error(e));
    } catch (err: any) {
      console.error('Error cargando datos del dashboard:', err);
      if (err.response?.status >= 500) {
        setError('Error al cargar los datos del dashboard. Reintente en unos momentos.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Recarga todos los datos
   */
  const refresh = useCallback(() => {
    loadAllData();
  }, [loadAllData]);

  // Cargar datos inicialmente
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  return {
    // Estados
    metrics,
    recentSales,
    salesChartData,
    topProducts,
    lowStockProducts,
    revenueComparison,
    loading,
    error,

    // Acciones
    refresh,
    loadMetrics,
    loadRecentSales,
    loadSalesChart,
    loadTopProducts,
    loadLowStockProducts
  };
};
